import IAdminApi from './IAdminApi';
import ApiResponse from '../entities/ApiResponse';
import { IPost } from '../../enteties/IPost';
import { IServerLanguage } from '../../enteties/IServerLanguage';
import { IType } from '../../enteties/IType';
import { getParameterString } from '../../functions/getParameterString';
import { IMetaData } from '../../enteties/IMetaData';
import { ApiFileCV } from '../entities/ApiFileCV';
import { ApiSendedCV, ApiVacancy, CVBody } from '../entities/ApiVacancies';
import { ApiSearcher } from '../entities/ApiSearcher';

export default class AdminApi implements IAdminApi {
  private baseUrl: string = process.env.REACT_APP_ADMIN_PANEL_API_HOST!;

  private async fetchData(path: string, requestOptions: any): Promise<any> {
    try {
      // console.log(`${this.getBaseUrl(path)}${path}`, { ...requestOptions });
      const response = await fetch(`${this.baseUrl}${path}`, {
        ...requestOptions,
      });
      const statusCode = response.status;
      const data = await response.json();

      return {
        data,
        statusCode,
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('FETCH ERROR:', e);
      throw new Error(`API Fetch error: ${e}`);
    }
  }

  private async getData(path: string, tokenRequired = true): Promise<any> {
    const myHeaders: { [key: string]: string } = {};

    if (tokenRequired) {
      const accessToken = localStorage.getItem('access_token');
      myHeaders.Authorization = `Bearer ${accessToken}`;
    }

    const requestOptions: {
      method: string;
      redirect: 'follow' | 'error' | 'manual' | undefined;
      headers: { [key: string]: string };
    } = {
      method: 'GET',
      redirect: 'follow',
      headers: myHeaders,
    };
    return this.fetchData(path, requestOptions);
  }

  private async postData(
    path: string,
    data?: any,
    formData?: any,
    tokenRequired = true,
  ): Promise<any> {
    const myHeaders: { [key: string]: string } = {};

    if (!formData) {
      myHeaders['Content-Type'] = 'application/json';
    }

    const requestOptions: {
      method: string;
      headers: { [key: string]: string };
      body: string;
      redirect: 'follow' | 'error' | 'manual' | undefined;

      mode: string;
      cache: string;
      credentials: string;
      referrerPolicy: string;
    } = {
      method: 'POST',
      headers: myHeaders,
      body: formData ? data : JSON.stringify(data),
      redirect: 'follow',

      mode: 'cors', // no-cors, *cors, same-origin
      cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
      credentials: 'same-origin', // include, *same-origin, omit
      referrerPolicy: 'no-referrer', // no-referrer, *client
    };

    return this.fetchData(path, requestOptions);
  }

  async putData(path: string, data: any, formData?: boolean) {
    const myHeaders: { [key: string]: string } = {};
    if (!formData) {
      myHeaders['Content-Type'] = 'application/json';
    }

    const requestOptions = {
      method: 'PUT',
      headers: myHeaders,
      body: formData ? data : JSON.stringify(data),
      redirect: 'follow',
    };

    return this.fetchData(path, requestOptions);
  }

  private async deleteData(path: string, data?: any): Promise<any> {
    const requestOptions: {
      method: string;
      redirect: 'follow' | 'error' | 'manual' | undefined;
      body: string;
    } = {
      method: 'DELETE',
      redirect: 'follow',
      body: JSON.stringify(data),
    };

    return this.fetchData(path, requestOptions);
  }

  // eslint-disable-next-line class-methods-use-this
  public async getTypes(): Promise<ApiResponse<IType[]>> {
    return Promise.resolve({
      statusCode: 200,
      data: [
        { id: 1, name: 'Other' },
        { id: 2, name: 'Blog' },
      ],
    });
    // return this.getData('/types/');
  }

  public async getLanguages(): Promise<ApiResponse<IServerLanguage[]>> {
    return this.getData('/languages/');
  }

  // eslint-disable-next-line class-methods-use-this
  public async getPosts(
    language?: string,
    filterParams?: { [key: string]: string | string[] },
    limit = 10,
    offset = 0,
  ): Promise<ApiResponse<{ data: IPost[]; count: number }>> {
    const path = `/a/?offset=${offset || 0}&limit=${limit || 10}${
      language ? `&language=${language}` : ''
    }${getParameterString(filterParams)}`;

    return this.getData(path);
  }

  // eslint-disable-next-line class-methods-use-this
  public async getPost(slug: string): Promise<ApiResponse<IPost>> {
    return this.getData(`/a/${slug}`);
  }

  // eslint-disable-next-line class-methods-use-this
  public async getMetaBySlug(slug: string): Promise<ApiResponse<IMetaData>> {
    return this.getData(`/meta_data/slug/${slug}`);
  }

  public async saveCVFile(data: { file: File }): Promise<ApiResponse<ApiFileCV>> {
    const formData = new FormData();
    formData.append('file', data.file);

    return this.postData('/files/cv', formData, true);
  }

  public async sendBasicCV(data: CVBody): Promise<ApiResponse<ApiSendedCV>> {
    return this.postData('/resumes/', data);
  }

  public async sendVacancyCV(id: number, data: CVBody): Promise<ApiResponse<ApiSendedCV>> {
    return this.postData(`/vacancies/${id}/responses`, data);
  }

  public async getVacancy(id: string): Promise<ApiResponse<ApiVacancy>> {
    return this.getData(`/vacancies/${id}`);
  }

  public async getVacancies(
    filterParams?: { [key: string]: string | string[] },
    limit?: number,
    offset?: number,
  ): Promise<ApiResponse<{ data: ApiVacancy[]; count: number }>> {
    const path = `/public/vacancies/?offset=${offset || 0}&limit=${limit || 10}${getParameterString(filterParams)}`;

    return this.getData(path);
  }

  public async searchPage(language: string, q: string, limit?: number, offset?: number): Promise<ApiResponse<ApiSearcher[]>> {
    const res = await this.getData(`/search?language=${language}&q=${q}&offset=${offset || 0}&limit=${limit || 10}`);

    return Promise.resolve({
      data: res.data.data,
      statusCode: res.statusCode,
      count: res.data.count,
    });
  }
}
